/**
 * Phần thuần của bước đặt mã khi tự mở tiệm (ADR-087) + cờ "vừa mở tiệm" để lần vào đầu hiện một câu chào.
 * Luật mã ở máy chủ (`lyDoMaKhongHopLe` gồm cả mã dễ đoán + đúng 6 số) là bản chính; ở đây chỉ để báo sớm, cùng ý.
 */
import { CAU_MA_DE_DOAN, chuanHoaSdtVn, laMaDeDoan, type NganhTiem } from '@sapa/shared'
import { ApiFailure } from './api.ts'
import { E } from './text/mo-tiem.ts'
import { T } from './text/vi.ts'

/** Vì sao hai ô mã chưa dùng được — `null` nếu dùng được. Mã đúng 6 số; hai lần nhập phải giống nhau. */
export function loiMaMoTiem(ma: string, nhapLai: string): string | null {
  if (!/^\d{6}$/.test(ma)) return E.maPhaiSauSo
  // Cùng hàm với máy chủ (`lyDoMaKhongHopLe`): báo sớm, khỏi đợi bấm "Mở tiệm" mới biết mã yếu.
  if (laMaDeDoan(ma)) return CAU_MA_DE_DOAN
  if (ma !== nhapLai) return T.pinMismatch
  return null
}

/**
 * Báo lệch NGAY ở ô "Nhập lại mã" (vòng 33 V1) thay vì đợi bấm "Mở tiệm": đủ 6 số mà khác ô trên, hoặc đã rời ô
 * (hoặc ô trên trống) mà còn khác. Đang gõ dở (chưa rời ô, chưa đủ 6 số) thì chưa báo, khỏi hù người đang gõ.
 */
export function loiMaLechSom(ma: string, nhapLai: string, daRoiO: boolean): string | null {
  if (nhapLai === '' || nhapLai === ma) return null
  if (nhapLai.length >= 6 || daRoiO || ma === '') return T.pinMismatch
  return null
}

/** Số điện thoại gõ vào form mở tiệm đã là số thật chưa — cùng hàm với máy chủ (`chuanHoaSdtVn`). */
export function sdtMoTiemHopLe(sdt: string): boolean {
  return chuanHoaSdtVn(sdt.trim()) !== null
}

/** Máy chủ từ chối vì số điện thoại (code VALIDATION + câu nói về số điện thoại) → đưa chủ tiệm về bước 1. */
export function laLoiSdtMayChu(err: unknown): boolean {
  return err instanceof ApiFailure && /số điện thoại/i.test(err.messageVi)
}

/**
 * Vì sao nút "Tiếp" ở bước 1 đang khoá — câu đầu tiên còn thiếu theo thứ tự các ô trên màn; `null` = đủ, bấm được.
 * (ui-critic 01/10: nút khoá mà không nói vì sao thì chủ tiệm kẹt.)
 */
export function lyDoChuaTiep(v: {
  tenTiem: string
  nganh: NganhTiem | null
  tenChu: string
  sdt: string
}): string | null {
  if (v.tenTiem.trim().length < 2) return E.thieuTenTiem
  if (v.nganh === null) return E.chonNganh
  if (v.tenChu.trim() === '') return E.thieuTenChu
  if (v.sdt.trim() === '') return E.thieuSdt
  if (!sdtMoTiemHopLe(v.sdt)) return E.sdtSai
  return null
}

const KHOA_VUA_MO = 'sapa.vuaMoTiem'

/**
 * Ghi nhớ trong phiên trình duyệt: vừa mở tiệm xong, thẻ "Chuẩn bị tiệm" giữ câu chào đầu thẻ cho tới khi chủ bấm
 * một bước hoặc đóng (`xoaVuaMoTiem`) — không còn là toast 10 giây dễ bỏ lỡ. Lỗi lưu thì bỏ qua.
 */
export function ghiVuaMoTiem(): void {
  try {
    sessionStorage.setItem(KHOA_VUA_MO, '1')
  } catch {
    // Không lưu được thì chỉ mất câu chào — vào làm việc vẫn bình thường.
  }
}

/** Còn lời chào "vừa mở tiệm" không. KHÔNG xoá cờ: tải lại trang vẫn còn tới khi chủ đóng. */
export function docVuaMoTiem(): boolean {
  try {
    return sessionStorage.getItem(KHOA_VUA_MO) === '1'
  } catch {
    return false
  }
}

/** Chủ đã bấm một bước hoặc đóng lời chào: gỡ cờ. */
export function xoaVuaMoTiem(): void {
  try {
    sessionStorage.removeItem(KHOA_VUA_MO)
  } catch {
    // Không xoá được thì lời chào hiện lại ở lần tải sau — vô hại.
  }
}
